import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import type {QRCodeFileExtension, QRPayloadCode, QRPayloadResult} from '@Libraries/qr-code/qr-code.types';
import {QRPayloadType} from '@Libraries/qr-code/qr-code.types';
import {QR_GENERATION_SIZE_PX} from '@Libraries/qr-code/qr-render.library';
import {hasBlockingQRPayloadIssue} from '@Libraries/qr-code/qr-payload.library';
import {downloadBlobAsFile} from '@Utils/file.util';
import {assertNever} from '@Utils/assert-never.util';
import {Text, TextSize} from '@Components/text';
import {QRTypeTabs} from '@Components/qr-code-generator/components/qr-type-tabs/qr-type-tabs';
import {QR_TYPE_TAB_PANEL_ID} from '@Components/qr-code-generator/components/qr-type-tabs/qr-type-tabs.types';
import {QRCodeResult} from '@Components/qr-code-generator/components/qr-code-result/qr-code-result';
import type {QRCodeCarouselControls} from '@Components/qr-code-generator/components/qr-code-carousel/qr-code-carousel.types';
import {QRStyleModal} from '@Components/qr-code-generator/components/qr-style-modal/qr-style-modal';
import {QRCustomizeMenu} from '@Components/qr-code-generator/components/qr-customize-menu/qr-customize-menu';
import {QRTextForm} from '@Components/qr-code-generator/components/qr-text-form/qr-text-form';
import {QRSocialForm} from '@Components/qr-code-generator/components/qr-social-form/qr-social-form';
import {QREmailForm} from '@Components/qr-code-generator/components/qr-email-form/qr-email-form';
import {QRPhoneForm} from '@Components/qr-code-generator/components/qr-phone-form/qr-phone-form';
import {QRSmsForm} from '@Components/qr-code-generator/components/qr-sms-form/qr-sms-form';
import {QRWifiForm} from '@Components/qr-code-generator/components/qr-wifi-form/qr-wifi-form';
import {QRVCardForm} from '@Components/qr-code-generator/components/qr-vcard-form/qr-vcard-form';
import {QRCalendarEventForm} from '@Components/qr-code-generator/components/qr-calendar-event-form/qr-calendar-event-form';
import {QRAppStoreForm} from '@Components/qr-code-generator/components/qr-app-store-form/qr-app-store-form';
import {QRLinkForm} from '@Components/qr-code-generator/components/qr-link-form/qr-link-form';
import {QRLinkFormVariant} from '@Components/qr-code-generator/components/qr-link-form/qr-link-form.types';
import type {QRCodeGeneratorProps, QRCodeStyle, QRGeneratorLogo, QRGeneratorState, QRPreviewStyle} from '@Components/qr-code-generator/qr-code-generator.types';
import {
  QR_SAMPLE_PAYLOAD,
  QR_DOWNLOAD_FILENAME_BY_VARIANT,
  QR_SAMPLE_FOREGROUND_COLOR,
  QR_SESSION_WRITE_DEBOUNCE_MS,
  QR_TOOL_TYPES,
  QRGeneratorActionKind,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {qrGeneratorReducer} from '@Components/qr-code-generator/qr-code-generator-reducer';
import {readQRGeneratorSession, writeQRGeneratorSession} from '@Components/qr-code-generator/qr-generator-session.library';
import {QRSuggestedTemplates} from '@Components/qr-code-generator/components/qr-suggested-templates/qr-suggested-templates';
import {withQRHandoffParam, writeQRHandoff} from '@Libraries/qr-code/qr-handoff.library';
import {getDuplicatePosterURL} from '@Libraries/poster-editor.library';
import {
  copyQRCodeToClipboard,
  getActiveLogoPlatform,
  getDesignTilesForType,
  getQRCodeVariantLabel,
  getTypeTabLabel,
  getQRHandoffItemObject,
  getPayloadLevelIssueText,
  getPreviewPayload,
  getHandoffLogoReference,
  getResultForActiveType,
  getVisibleCodeIndex,
  isShowingSampleQRCode,
  renderStyledQRCodeBlob,
} from '@Components/qr-code-generator/qr-code-generator.library';
import {useQRCodePreviews, useSocialPlatformLogo} from '@Components/qr-code-generator/qr-code-generator-hooks';
import type {QRSocialPlatform} from '@Libraries/qr-code/qr-social-platform.library';
import {
  QRCopyResult,
  QRLogoSource,
  trackQRCopy,
  trackQRDesignSelected,
  trackQRDownload,
  trackQRGalleryClick,
  trackQRLogoAdded,
  trackQRStyleApplied,
  trackQRTypeSelected,
} from '@Components/qr-code-generator/qr-generator-tracking.library';
import styles from './qr-code-generator.module.scss';

/**
 * The free QR tool on a marketing landing page: pick a code type, fill its form, style the code, then download it
 * or carry it into the poster editor on one of the page's designs.
 */
export function QRCodeGenerator({socialIconBaseUrl, templateTileSets, defaultActiveType}: QRCodeGeneratorProps): ReactElement {
  const [mountedAt] = useState(() => new Date());
  const [state, dispatch] = useReducer(qrGeneratorReducer, mountedAt, (stampedAt: Date): QRGeneratorState => {
    return readQRGeneratorSession(stampedAt, defaultActiveType);
  });

  /**
   * Debounced, because every keystroke dispatches: serialising the whole tool's state into session storage on each one is work the visitor
   * pays for while typing. A pending write is cancelled by the next change, so a burst of typing costs one write instead of one per letter.
   * `pagehide` writes straight away, so leaving the page inside the debounce window still saves what was typed — which is the point of
   * keeping the session at all.
   */
  useEffect((): (() => void) => {
    const writeSession = (): void => {
      writeQRGeneratorSession(state);
    };
    const timeoutId = window.setTimeout(writeSession, QR_SESSION_WRITE_DEBOUNCE_MS);
    window.addEventListener('pagehide', writeSession);
    return (): void => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('pagehide', writeSession);
    };
  }, [state]);

  const result = useMemo<QRPayloadResult>((): QRPayloadResult => {
    return getResultForActiveType(state);
  }, [state]);
  const [requestedCodeIndex, setRequestedCodeIndex] = useState(0);
  const visibleCodeIndex = getVisibleCodeIndex(requestedCodeIndex, result.codes.length);
  const activeCode = result.codes[visibleCodeIndex] ?? result.codes[0];
  const isCodeBlocked = hasBlockingQRPayloadIssue(result);
  const payloadIssueText = getPayloadLevelIssueText(result.issues);

  const logoPlatform = getActiveLogoPlatform(state.activeType, activeCode.payload);
  const platformLogoDataUrl = useSocialPlatformLogo(logoPlatform, socialIconBaseUrl);

  const previewStyle = useMemo<QRPreviewStyle>(() => {
    return {...state.style, logoDataUrl: state.logo?.dataUrl ?? platformLogoDataUrl};
  }, [state.style, state.logo, platformLogoDataUrl]);

  const isShowingSample = useMemo<boolean>((): boolean => {
    return isShowingSampleQRCode(state, activeCode.payload);
  }, [state, activeCode.payload]);
  const getCodePreviewPayload = (payload: string): string => {
    return isShowingSample ? QR_SAMPLE_PAYLOAD : getPreviewPayload(payload);
  };

  /**
   * Only what is on screen is greyed. Downloads, the clipboard and the hand-off keep {@link previewStyle}, so the disabled look can never
   * reach a code the visitor actually takes away.
   */
  const displayStyle: QRPreviewStyle = isShowingSample ? {...previewStyle, foregroundColor: QR_SAMPLE_FOREGROUND_COLOR} : previewStyle;

  const codePayloads = result.codes.map((code): string => {
    return getCodePreviewPayload(code.payload);
  });
  const {previewUrls, isRendering} = useQRCodePreviews(codePayloads, displayStyle);

  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [hasHandoffFailed, setHasHandoffFailed] = useState(false);

  const onStyleOpen = useCallback((): void => {
    setIsStyleModalOpen(true);
  }, []);

  const onStyleClose = useCallback((): void => {
    setIsStyleModalOpen(false);
  }, []);

  const onStyleCommit = useCallback(
    (style: QRCodeStyle): void => {
      trackQRStyleApplied(state.activeType, state.style, style);
      dispatch({kind: QRGeneratorActionKind.PATCH_STYLE, patch: style});
      setIsStyleModalOpen(false);
    },
    [state.activeType, state.style]
  );

  const onDownload = useCallback(
    async (code: QRPayloadCode, extension: QRCodeFileExtension): Promise<void> => {
      const blob = await renderStyledQRCodeBlob({payload: code.payload, sizePx: QR_GENERATION_SIZE_PX, ...previewStyle}, extension);
      downloadBlobAsFile(blob, `${QR_DOWNLOAD_FILENAME_BY_VARIANT[code.variant]}.${extension}`);
      trackQRDownload(state.activeType, extension);
    },
    [previewStyle, state.activeType]
  );

  const onCopy = useCallback(
    async (code: QRPayloadCode): Promise<void> => {
      try {
        await copyQRCodeToClipboard({payload: code.payload, sizePx: QR_GENERATION_SIZE_PX, ...previewStyle});
      } catch (error) {
        trackQRCopy(state.activeType, QRCopyResult.FAILED);
        throw error;
      }
      trackQRCopy(state.activeType, QRCopyResult.OK);
    },
    [previewStyle, state.activeType]
  );

  const onLogoChange = useCallback(
    (logo: QRGeneratorLogo | null): void => {
      if (logo !== null) {
        trackQRLogoAdded(state.activeType, QRLogoSource.UPLOAD);
      }
      dispatch({kind: QRGeneratorActionKind.SET_LOGO, logo});
    },
    [state.activeType]
  );

  /**
   * A platform mark lands without anyone asking for it, so it is reported once per platform rather than on every render that keeps it —
   * and only once its icon has actually resolved and no uploaded logo is covering it, which is the only state in which the code carries it.
   * The icon is fetched, so gating on the platform alone would report a logo that a failed request means the visitor never got.
   */
  const reportedLogoPlatformsRef = useRef<Set<QRSocialPlatform>>(new Set());
  useEffect((): void => {
    if (logoPlatform === null || platformLogoDataUrl === null || state.logo !== null || reportedLogoPlatformsRef.current.has(logoPlatform)) {
      return;
    }
    reportedLogoPlatformsRef.current.add(logoPlatform);
    trackQRLogoAdded(state.activeType, QRLogoSource.PLATFORM);
  }, [logoPlatform, platformLogoDataUrl, state.logo, state.activeType]);

  /**
   * The tab is opened before the hand-off is stashed, not after: stashing awaits IndexedDB, and a tab opened once that promise has
   * resolved is no longer attributable to the click, which is what pop-up blockers drop. A blocker that refuses even the synchronous
   * open leaves the visitor with no design at all, so that case falls back to this tab.
   */
  const onDesignSelect = useCallback(
    async (designHashedId: string): Promise<void> => {
      setHasHandoffFailed(false);
      const editorTab = window.open('', '_blank');
      const wasHandedOff = await writeQRHandoff(getQRHandoffItemObject(activeCode.payload, previewStyle, getHandoffLogoReference(state.logo, logoPlatform)));

      if (!wasHandedOff) {
        editorTab?.close();
        setHasHandoffFailed(true);
        return;
      }

      trackQRDesignSelected(state.activeType);
      const editorUrl = withQRHandoffParam(getDuplicatePosterURL(designHashedId));
      if (editorTab === null) {
        window.location.assign(editorUrl);
        return;
      }
      editorTab.location.assign(editorUrl);
    },
    [activeCode.payload, previewStyle, state.logo, logoPlatform, state.activeType]
  );

  const renderHandoffError = (): ReactElement | null => {
    if (!hasHandoffFailed) {
      return null;
    }
    return <Text val={window.i18next.t('pmwjs_qr_generator_handoff_failed')} size={TextSize.SM} className={styles.qrHandoffError} attributes={{role: 'alert'}} />;
  };

  const renderCustomizeMenu = (): ReactElement => {
    return <QRCustomizeMenu logo={state.logo} onLogoChange={onLogoChange} onStyleOpen={onStyleOpen} />;
  };

  const getCarouselControls = (): QRCodeCarouselControls | null => {
    if (result.codes.length < 2) {
      return null;
    }
    return {
      codeCount: result.codes.length,
      visibleIndex: visibleCodeIndex,
      getCodeAriaLabel: (index: number): string => {
        return window.i18next.t('pmwjs_qr_generator_show_code', {code: getQRCodeVariantLabel(result.codes[index].variant) ?? ''});
      },
      onSelectIndex: setRequestedCodeIndex,
    };
  };

  const renderCodeResults = (): ReactElement => {
    return (
      <div className={styles.qrCodeResults}>
        <QRCodeResult
          key={activeCode.variant}
          code={activeCode}
          previewUrl={previewUrls[visibleCodeIndex] ?? null}
          isRendering={isRendering}
          isCodeBlocked={isCodeBlocked}
          payloadIssueText={payloadIssueText}
          variantLabel={getQRCodeVariantLabel(activeCode.variant)}
          carousel={getCarouselControls()}
          customizeControl={renderCustomizeMenu()}
          onDownload={onDownload}
          onCopy={onCopy}
        />
      </div>
    );
  };

  const renderStyleModal = (): ReactElement | null => {
    if (!isStyleModalOpen) {
      return null;
    }
    return (
      <QRStyleModal
        style={state.style}
        previewPayload={getCodePreviewPayload(activeCode.payload)}
        logoDataUrl={previewStyle.logoDataUrl}
        onCommit={onStyleCommit}
        onClose={onStyleClose}
      />
    );
  };

  const renderActiveForm = (): ReactElement => {
    switch (state.activeType) {
      case QRPayloadType.URL:
        return (
          <QRLinkForm
            variant={QRLinkFormVariant.WEB_ADDRESS}
            input={state.urlInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_URL, patch});
            }}
          />
        );
      case QRPayloadType.SOCIAL:
        return (
          <QRSocialForm
            input={state.socialInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_SOCIAL, patch});
            }}
          />
        );
      case QRPayloadType.TEXT:
        return (
          <QRTextForm
            input={state.textInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_TEXT, patch});
            }}
          />
        );
      case QRPayloadType.EMAIL:
        return (
          <QREmailForm
            input={state.emailInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_EMAIL, patch});
            }}
          />
        );
      case QRPayloadType.PHONE:
        return (
          <QRPhoneForm
            input={state.phoneInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_PHONE, patch});
            }}
          />
        );
      case QRPayloadType.SMS:
        return (
          <QRSmsForm
            input={state.smsInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_SMS, patch});
            }}
          />
        );
      case QRPayloadType.WIFI:
        return (
          <QRWifiForm
            input={state.wifiInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_WIFI, patch});
            }}
          />
        );
      case QRPayloadType.VCARD:
        return (
          <QRVCardForm
            input={state.vCardInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_VCARD, patch});
            }}
          />
        );
      case QRPayloadType.CALENDAR_EVENT:
        return (
          <QRCalendarEventForm
            input={state.calendarEventInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_CALENDAR_EVENT, patch});
            }}
          />
        );
      case QRPayloadType.PAYMENT_LINK:
        return (
          <QRLinkForm
            variant={QRLinkFormVariant.PAYMENT_LINK}
            input={state.paymentLinkInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_PAYMENT_LINK, patch});
            }}
          />
        );
      case QRPayloadType.APP_STORE:
        return (
          <QRAppStoreForm
            input={state.appStoreInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_APP_STORE, patch});
            }}
          />
        );
      case QRPayloadType.MULTIMEDIA:
        return (
          <QRLinkForm
            variant={QRLinkFormVariant.MULTIMEDIA}
            input={state.multimediaInput}
            issues={result.issues}
            onPatch={(patch): void => {
              dispatch({kind: QRGeneratorActionKind.PATCH_MULTIMEDIA, patch});
            }}
          />
        );
      default:
        return assertNever(state.activeType);
    }
  };

  return (
    <div className={styles.qrToolCard}>
      <div className={styles.qrToolHero}>
        <div className={styles.qrToolRail}>
          <QRTypeTabs
            types={QR_TOOL_TYPES}
            activeType={state.activeType}
            onTypeSelect={(activeType): void => {
              trackQRTypeSelected(activeType);
              dispatch({kind: QRGeneratorActionKind.SET_ACTIVE_TYPE, activeType});
            }}
          />
        </div>

        <div className={styles.qrToolBody}>
          <div
            id={QR_TYPE_TAB_PANEL_ID}
            className={styles.qrFormStack}
            role="tabpanel"
            aria-label={getTypeTabLabel(state.activeType)}
          >
            {renderActiveForm()}
          </div>

          <div className={styles.qrPreviewColumn}>{renderCodeResults()}</div>
        </div>
      </div>

      <QRSuggestedTemplates
        tiles={getDesignTilesForType(templateTileSets, state.activeType)}
        blockClasses={styles.qrTemplatesBlock}
        onTemplateSelect={onDesignSelect}
        onBrowseAllClick={(): void => {
          trackQRGalleryClick(state.activeType);
        }}
      />
      {renderHandoffError()}
      {renderStyleModal()}
    </div>
  );
}
